/**
 * Create a salon session from face-identity profile and navigate to /capture.
 * Skips landing + user-info when the guest already checked in via face.
 */

import { readFaceProfileSnapshot, updateFaceUserSalonReference } from '../../face-identity/utils/faceIdentityApi';
import { getSalonGuestPiiField, getSalonLastCustomerSnapshot } from './salonGuestPiiStore';
import { normalizeSalonGenderForApi } from './salonGender';
import {
  getSalonFaceUserId,
  getSalonComparePreviousFlag,
  isSalonFaceCheckedIn,
} from './salonFaceSession';
import {
  getFranchiseId,
  getFranchiseOrganizationId,
  getFranchiseStylistId,
} from '../../shared/franchiseFlowAuth';
import { captureDeviceInfo } from './deviceCapture';
import { getApiBaseUrl } from './apiConfig';
import { createNewSessionWithBackend, pathWithSessionId } from './sessionManager';
import { resolveAge } from './ageFromDob';

import {
  getActiveCheckinSession,
  getSalonFaceOrg,
  getSalonVisitReference,
  getSalonEntrySource,
} from './salonFaceSession';
import { readVisitHandoff } from './visitHandoff';
/**
 * Collect face profile fields from route state + sessionStorage.
 * @param {{ faceUserId?: string, faceProfile?: object, comparePrevious?: boolean, token?: string, deviceInfo?: object } | null} locationState
 */
export function readFaceSalonProfile(locationState = null) {
  const handoff = readVisitHandoff();
  const stored = readFaceProfileSnapshot() || {};
  const user = locationState?.faceProfile?.user || stored?.user || {};
  const profile = locationState?.faceProfile?.profile || stored?.profile || user?.profile || {};
  const preferences =
    (profile?.preferences && typeof profile.preferences === 'object' ? profile.preferences : {}) || {};

  const candidates = [
    locationState?.faceUserId,
    stored?.faceUserId,
    user?.id,
    getSalonFaceUserId(),
    handoff?.faceUserId,
  ];
  let faceUserId = '';
  for (const candidate of candidates) {
    const id = String(candidate || '').trim();
    if (id && !id.startsWith('checkin:')) {
      faceUserId = id;
      break;
    }
  }

  const name =
    user?.name ||
    user?.full_name ||
    locationState?.customerName ||
    handoff?.customerName ||
    getSalonGuestPiiField('name') ||
    '';
  const mobile =
    user?.mobile ||
    user?.phone ||
    locationState?.mobile ||
    handoff?.mobile ||
    getSalonGuestPiiField('mobile') ||
    '';
  const email =
    user?.email ||
    locationState?.email ||
    handoff?.email ||
    getSalonGuestPiiField('email') ||
    '';
  const gender =
    user?.gender ||
    profile?.gender ||
    preferences?.gender ||
    preferences?.Gender ||
    locationState?.gender ||
    handoff?.gender ||
    getSalonGuestPiiField('gender') ||
    '';
  const dob =
    preferences?.date_of_birth ||
    preferences?.dob ||
    user?.date_of_birth ||
    user?.dob ||
    locationState?.dob ||
    handoff?.dob ||
    getSalonGuestPiiField('dob') ||
    '';
  const ageStored =
    user?.age != null && String(user.age).trim() !== ''
      ? user.age
      : locationState?.age != null && String(locationState.age).trim() !== ''
        ? locationState.age
        : handoff?.age != null && String(handoff.age).trim() !== ''
          ? handoff.age
          : getSalonGuestPiiField('age') || '';
  const age = resolveAge(ageStored, dob);

  const comparePrevious =
    locationState?.comparePrevious === true ||
    getSalonComparePreviousFlag();

  const previousSalonReference =
    (comparePrevious &&
      (user?.last_salon_reference ||
        user?.lastSalonReference ||
        locationState?.faceProfile?.user?.last_salon_reference ||
        '')) ||
    '';

  return {
    faceUserId: faceUserId ? String(faceUserId) : '',
    name: name ? String(name) : '',
    mobile: mobile ? String(mobile) : '',
    email: email ? String(email) : '',
    gender: gender ? String(gender) : '',
    dateOfBirth: dob ? String(dob) : '',
    age,
    comparePrevious: Boolean(comparePrevious),
    previousSalonReference: previousSalonReference ? String(previousSalonReference).trim() : '',
    skinType: user?.skin_type || user?.skinType || '',
  };
}

export function isFaceCheckedIn(locationState = null) {
  if (isSalonFaceCheckedIn()) return true;
  const handoff = readVisitHandoff();
  if (handoff?.checkinId && (handoff.customerName || handoff.faceUserId)) return true;
  if (locationState?.checkinId && (locationState?.customerName || locationState?.faceUserId)) {
    return true;
  }
  // Returning guest with a real face user id (not a synthetic checkin: placeholder).
  const faceId = String(
    locationState?.faceUserId ||
      handoff?.faceUserId ||
      readFaceSalonProfile(locationState).faceUserId ||
      '',
  ).trim();
  if (faceId && !faceId.startsWith('checkin:')) return true;
  return false;
}

/**
 * Build camelCase userData for capture → analysis → save, merging route state + face/handoff profile.
 */
export function resolveGuestUserDataForAnalysis(locationState = null) {
  const st = locationState || {};
  const fromUserData =
    st.userData && typeof st.userData === 'object' ? st.userData : {};
  const profile = readFaceSalonProfile(st);
  const name =
    fromUserData.name ||
    st.name ||
    st.customerName ||
    profile.name ||
    '';
  const mobileNumber =
    fromUserData.mobileNumber ||
    fromUserData.mobile_number ||
    st.mobileNumber ||
    st.mobile ||
    st.phone ||
    profile.mobile ||
    '';
  const email =
    fromUserData.email || st.email || profile.email || '';
  const handoff = readVisitHandoff() || {};
  const storedSnap = readFaceProfileSnapshot() || st.faceProfile || {};
  const profileBlock = storedSnap.profile || storedSnap.user?.profile || {};
  const prefs =
    profileBlock?.preferences && typeof profileBlock.preferences === 'object'
      ? profileBlock.preferences
      : {};
  const gender =
    fromUserData.gender ||
    st.gender ||
    profile.gender ||
    prefs.gender ||
    prefs.Gender ||
    handoff.gender ||
    getSalonGuestPiiField('gender') ||
    null;
  const age =
    fromUserData.age ?? st.age ?? profile.age ?? null;
  const skinType =
    fromUserData.skinType ||
    fromUserData.skin_type ||
    st.skinType ||
    profile.skinType ||
    null;
  const faceUserId =
    fromUserData.faceUserId ||
    fromUserData.face_user_id ||
    st.faceUserId ||
    profile.faceUserId ||
    '';
  const dob =
    fromUserData.dob ||
    fromUserData.dateOfBirth ||
    st.dob ||
    profile.dateOfBirth ||
    '';

  return {
    name: name ? String(name) : '',
    mobileNumber: mobileNumber ? String(mobileNumber) : '',
    email: email ? String(email) : '',
    gender: gender ? String(gender) : null,
    age: age != null && String(age).trim() !== '' ? age : null,
    skinType: skinType ? String(skinType) : null,
    faceUserId: faceUserId ? String(faceUserId) : '',
    dob: dob ? String(dob) : '',
    instagramId: fromUserData.instagramId || st.instagramId || '',
  };
}

/** `male` | `female` | '' for franchise price-list filtering. */
export function normalizeSalonGuestGender(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (['male', 'm', 'man', 'men'].includes(raw)) return 'male';
  if (['female', 'f', 'woman', 'women'].includes(raw)) return 'female';
  return '';
}

/** Guest gender from profile, check-in handoff, visit snapshot, and kiosk PII store. */
export function resolveSalonGuestGender(locationState = null, extra = {}) {
  const st = locationState || {};
  const fromProfile = resolveGuestUserDataForAnalysis(st);
  const faceSalon = readFaceSalonProfile(st);
  const handoff = readVisitHandoff() || {};
  const lastCustomer = getSalonLastCustomerSnapshot() || {};
  const visitGender = extra.visitGender || extra.visit_gender || '';
  const activeCheckinId = String(
    extra.checkinId ||
      st.checkinId ||
      getActiveCheckinSession()?.id ||
      handoff.checkinId ||
      '',
  ).trim();
  const handoffMatchesVisit =
    !activeCheckinId || String(handoff.checkinId || '') === activeCheckinId;
  const lastCustomerMatchesVisit =
    !activeCheckinId || String(lastCustomer.checkinId || '') === activeCheckinId;
  const storedSnap = readFaceProfileSnapshot() || {};
  const snapUser = storedSnap.user || st.faceProfile?.user || {};
  const candidates = [
    visitGender,
    st.userData?.gender,
    snapUser.gender,
    st.faceProfile?.user?.gender,
    st.faceProfile?.profile?.gender,
    st.gender,
    fromProfile.gender,
    faceSalon.gender,
    handoffMatchesVisit ? handoff.gender : '',
    handoffMatchesVisit ? getSalonGuestPiiField('gender') : '',
    lastCustomerMatchesVisit ? lastCustomer.gender : '',
  ];
  for (const c of candidates) {
    const normalized = normalizeSalonGuestGender(c) || normalizeSalonGenderForApi(c);
    if (normalized) return normalized;
  }
  return '';
}

/**
 * Create salon session from face profile.
 * @param {{ token?: string|null, deviceInfo?: object|null, locationState?: object|null }} options
 * @returns {Promise<{ sessionId: string, profile: object }>}
 */
export async function createSalonSessionFromFace(options = {}) {
  const locationState = options.locationState || null;
  const profile = readFaceSalonProfile(locationState);
  if (!profile.faceUserId && !profile.mobile && !profile.name) {
    throw new Error('Face profile is missing. Please check in again.');
  }

  const apiBase = getApiBaseUrl() || '';
  const token = options.token || locationState?.token || null;
  const deviceInfo = options.deviceInfo || locationState?.deviceInfo || captureDeviceInfo();

  const sessionPayload = {
    app_session_token: token || undefined,
    device_info: deviceInfo,
    face_user_id: profile.faceUserId || undefined,
    name: profile.name || undefined,
    mobile_number: profile.mobile || undefined,
    email: profile.email || undefined,
    gender: profile.gender || undefined,
    date_of_birth: profile.dateOfBirth || undefined,
    age: profile.age != null ? profile.age : undefined,
    skin_type: profile.skinType || undefined,
    organization_id: getFranchiseOrganizationId() || getSalonFaceOrg() || undefined,
    franchise_id: getFranchiseId() || undefined,
    stylist_id: getFranchiseStylistId() || undefined,
    visit_reference: getSalonVisitReference() || locationState?.visitReference || undefined,
    entry_source: getSalonEntrySource() || locationState?.entrySource || undefined,
  };

  let sessionId;
  try {
    sessionId = await createNewSessionWithBackend(apiBase, {
      ...sessionPayload,
      previous_salon_reference: profile.previousSalonReference || undefined,
    });
  } catch (err) {
    // Prior ref may be stale — still start a fresh session from face profile.
    if (profile.previousSalonReference) {
      sessionId = await createNewSessionWithBackend(apiBase, sessionPayload);
    } else {
      throw err;
    }
  }

  if (!sessionId) {
    throw new Error('Could not start salon session.');
  }

  try {
    if (profile.faceUserId) {
      await updateFaceUserSalonReference(profile.faceUserId, sessionId);
    }
  } catch (_) {
    /* non-blocking */
  }

  return { sessionId, profile };
}

/**
 * Bootstrap face → capture navigation.
 * @param {Function} navigate - react-router navigate
 * @param {{ token?: string|null, deviceInfo?: object|null, locationState?: object|null, replace?: boolean }} options
 */
export async function bootstrapFaceSalonToCapture(navigate, options = {}) {
  const { sessionId, profile } = await createSalonSessionFromFace(options);
  const path = pathWithSessionId('/capture', sessionId);
  navigate(path, {
    replace: options.replace !== false,
    state: {
      sessionId,
      gender: profile.gender || options.locationState?.gender || '',
      age: profile.age ?? options.locationState?.age,
      name: profile.name || options.locationState?.customerName || '',
      mobileNumber: profile.mobile || options.locationState?.mobile || '',
      email: profile.email || options.locationState?.email || '',
      skinType: profile.skinType || '',
      faceUserId: profile.faceUserId || '',
      checkinId: options.locationState?.checkinId || null,
      customerName: profile.name || options.locationState?.customerName || '',
      dob: profile.dateOfBirth || options.locationState?.dob || '',
      faceProfile: options.locationState?.faceProfile || undefined,
      ...(options.token ? { token: options.token } : {}),
    },
  });
  return sessionId;
}
